import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useScrollLock } from '../hooks/useScrollLock';
import { C, F, Mon, Bod, Dot, LangSeg, SH, LBL, HEAD, SCRIM, anim, trans } from './mobUI';
import { GithubMark } from '../config/design';
import { ALLOWED_POOLS } from '../config/constants';
import I18nLock from '../components/I18nLock';

const REPOS = [
  { url: 'https://github.com/Misha-btc/Acai',      label: 'Misha-btc/Acai' },
  { url: 'https://github.com/sorrow8/arbuz-child', label: 'sorrow8/arbuz-child' },
  { url: 'https://github.com/sorrow8/arbuz',       label: 'sorrow8/arbuz' },
];

const CONTENT = {
  en: {
    repoDescs: ['Arbuz token contract', 'Card factory contract', 'Card generator contract'],
    kicker:      'FIELD GUIDE',
    back:        '← BACK',
    nav: [
      { n: '01', id: 'overview', label: 'Overview'  },
      { n: '02', id: 'minting',  label: 'Arbuz Mint' },
      { n: '03', id: 'deck',     label: 'Card Mint'  },
      { n: '04', id: 'fees',     label: 'Fees'       },
      { n: '05', id: 'risks',    label: 'Risks'      },
    ],
    specs: [
      ['Protocol',      'Alkanes · Bitcoin L1'],
      ['ARBUZ id',      '2 : 25349'],
      ['Card Collection id', '2 : 69849'],
      ['Card Mint window',   'Every 144 blocks (~24h)'],
      ['First window',  'Block 907,205'],
      ['Card cost',     '100 Arbuz'],
      ['Acai Contract Requirement', '1,069 sats per Arbuz mint'],
    ],
    overview: {
      lead: 'A short field guide to minting, burning, and the deck.',
      learnCta: 'Learn how Arbuzino & Wunsch work',
      tldr:       'TL;DR',
      specsTitle:  'Specifications',
      p1: <>Arbuz is a Bitcoin alkane deployed on <a href="https://x.com/RebarLabs/status/1929196559687725334" target="_blank" rel="noopener noreferrer">Acai Bitcoin Hackathon 2025 winner contract ↗</a> - minted through specific Bitcoin mining pools. Every Arbuz mint also mints <b>Diesel</b> and <b>OG Rune</b>, regardless of outcome.</>,
      p2: 'If you decide to burn 100 Arbuz when the mint window opens, the protocol returns a Magic Arbuz Card from the deck.',
      p3: 'Each card carries a unique prediction. Its art, its traits, and its prophecy are derived from a hash of the card index, on-chain.',
      p4: "The generative art isn't hosted on a server - it's JavaScript frozen inside the Bitcoin blockchain. When you open a card, the app fetches that code from the chain and runs it live in your browser. The animation and the prediction execute directly from Bitcoin.",
      steps: [
        { n: '1', title: 'MINT ARBUZ TOKEN',      body: '330 sats + 1,069 sats Acai contract requirement + network fee. Choose Rebar (private) or Public mempool.' },
        { n: '2', title: 'MINT MAGIC ARBUZ CARD', body: <>During an open window (every 144 blocks), burn 100 Arbuz. The contract validates timing via <a href="https://github.com/JinMaa/clock-in" target="_blank" rel="noopener noreferrer">clock-in ↗</a>.</> },
        { n: '3', title: 'HOLD OR SEND',          body: 'Cards are alkanes - transferable on Bitcoin L1. Each carries a unique prediction and generative art.' },
      ],
    },
    minting: {
      howTitle:  'How it works',
      howBody:   <>Each Arbuz token mint builds a PSBT with one inscription output (330 sats), one treasury output (1,069 sats Acai contract requirement), and a protostone calling <b>opcode 77</b> on the Arbuz contract (2 : 25349). On success, the contract mints <b>100 Arbuz</b> alkane to your address. Every Arbuz mint also mints <b>Diesel</b> (the alkanes genesis token) and <b>OG Rune</b> - even if the Arbuz mint fails.</>,
      routingTitle: 'Routing',
      routing: [
        { name: 'Rebar - private pool',  color: C.rind,  body: <><b>Currently disabled.</b> Your signed transaction is submitted to Rebar Shield instead of the public mempool. Rebar routes it to a partner mining pool. The Rebar fee output is included in the PSBT so you pay it directly. <b>~80% success rate</b> - see Risks.</> },
        { name: 'Public - open mempool', color: C.melon, body: 'Your transaction is broadcast to the public mempool. Any miner can include it - but only approved pools will result in a successful mint. You never know whose block comes next, so the risk is much higher.' },
      ],
      outputsTitle: 'Transaction outputs',
      outputs: [
        ['Output 0', '330 sats → your taproot address (inscription)'],
        ['Output 1', 'Protostone (0 sats) - calls opcode 77'],
        ['Output 2', '1,069 sats → Acai Contract Requirement'],
        ['Output 3', 'Change → your payment address'],
        ['Output 4', 'Rebar fee (Rebar mode only)'],
      ],
      ripeKick: 'MINT STATUSES',
      ripe: [
        { tag: 'JUICY',    c: C.rind,  body: 'Mint confirmed on-chain - fresh 100 ARBUZ credited to your patch.' },
        { tag: 'RIPENING', c: C.yellow, body: 'Still in the mempool, waiting for an approved pool to confirm it.' },
        { tag: 'SPOILED',  c: C.melon, body: "Mined by an unapproved pool - the mint reverted. The fee is not refunded." },
      ],
    },
    deck: {
      genTitle:  'Card generation',
      genBody:   <>Each card's traits are derived from a <b>SHA-256 hash</b> of its index. The hash determines background, card type, main symbol, three mystical symbols, border colour, glow colour and your daily prediction. The full rendering JS is stored on-chain and fetched via <b>opcode 1000</b> on contract (2 : 69849).</>,
      typesTitle: 'Card types',
      types: [
        { name: 'Classic',  chance: 'majority',  body: '22 major arcana - Star, Moon, Sun, Tower, Wheel, Hermit, Magician, Priestess, Emperor, Empress, Devil, Fool, Hierophant, Lovers, Chariot, Strength, Justice, Hanged Man, Death, Temperance, Judgement, World.' },
        { name: 'Glitch',   chance: '~13 / 256', body: '8 special cards referencing the alkanes ecosystem - Airhead, Mist, Puppet, Taco, Acai, Diesel, Clockin, Cheekyb. Always gold border and glow.' },
        { name: 'Absolute', chance: '< 1 / 256', body: 'Ultra-rare. Only two possible: Fartane (silver) or Arbuz (green). Determined by hash byte 25 < 1.' },
      ],
      windowTitle: 'Mint window',
      windowBody:  <>The card contract calls <b>clock-in</b> (opcode 103) before minting. Windows open every <b>144 blocks</b> starting from block 907,205. Miss the window - the transaction fails and you keep your Arbuz token.</>,
      predTitle: 'Prediction',
      predBody:  'Every card carries a unique prediction - derived from the same on-chain hash that generates the art. Tap the card to toggle the prediction between English and Chinese.',
    },
    fees: {
      arbuzTitle: 'Minting ARBUZ',
      arbuzRows: [
        ['Inscription output', '330 sats (to your address)'],
        ['Acai Contract Requirement', '1,069 sats (every mint)'],
        ['Network fee',        '~251 vB × fee rate'],
        ['Rebar fee',          '~251 vB × Rebar rate (Rebar mode only)'],
        ['Total (public)',     '1,399 sats + network fee'],
      ],
      cardTitle: 'Minting a card',
      cardRows: [
        ['Inscription output', '330 sats (to your address)'],
        ['ARBUZ alkane',       '100 Arbuz burned (held by contract)'],
        ['Network fee',        '~300 vB × fee rate'],
        ['Total',              '330 sats + 100 Arbuz + network fee'],
      ],
    },
    risks: {
      poolsTitle: 'Allowed mining pools for Arbuz Token',
      poolsBody:  'The Acai contract validates the mining pool that includes the transaction without using 3rd parties - it\'s a pure smart contract function that can\'t be influenced by anyone since the contract is already deployed on-chain. Only blocks mined by approved pools succeed. Other pools fail - you lose the fee you sent.',
      publicTitle: 'Public mint success rate',
      publicBody:  'Although you can submit a public mint for just a few sats, your chances of losing are much higher - you never know whose block comes next. Any unsupported pool can mine the next block and your mint fails.',
      rebarTitle: 'Rebar success rate',
      rebarBody:  '~20% of blocks are still mined by unsupported pools. If your transaction lands in an unsupported block it fails. The fee is still paid.',
      timingTitle: 'Card mint timing',
      timingBody:  'If the transaction is included outside the 144-block window, the mint fails - you keep the Arbuz alkane. Submit just before the window opens with a fee rate high enough to land in the target block.',
      utxoTitle: 'Mixed UTXOs',
      utxoBody:  'If the selected UTXO holds other assets, the contract returns them automatically - only 100 ARBUZ is consumed.',
    },
  },
  zh: {
    repoDescs: ['Arbuz 代币合约', '卡牌工厂合约', '卡牌生成器合约'],
    kicker:      '使用指南',
    back:        '← 返回',
    nav: [
      { n: '01', id: 'overview', label: '概述'  },
      { n: '02', id: 'minting',  label: 'Arbuz 铸造' },
      { n: '03', id: 'deck',     label: '卡片铸造'   },
      { n: '04', id: 'fees',     label: '费用'   },
      { n: '05', id: 'risks',    label: '风险'   },
    ],
    specs: [
      ['协议',     'Alkanes · 比特币 L1'],
      ['ARBUZ id', '2 : 25349'],
      ['集合 id',  '2 : 69849'],
      ['铸造窗口', '每 144 个区块（~24小时）'],
      ['首个窗口', '第 907,205 区块'],
      ['卡片成本', '100 Arbuz'],
      ['Acai 合约要求', '每次铸造 1,069 sats'],
    ],
    overview: {
      lead: '关于铸造、销毁与卡组的简明指南。',
      learnCta: '了解 Arbuzino 与 Wunsch 的运作方式',
      tldr:        'TL;DR',
      specsTitle:  '规格参数',
      p1: <>Arbuz 是部署在 <a href="https://x.com/RebarLabs/status/1929196559687725334" target="_blank" rel="noopener noreferrer">Acai Bitcoin Hackathon 2025 获奖合约 ↗</a> 的比特币 alkane - 通过特定比特币矿池铸造。每次 Arbuz 铸造还会铸造 <b>Diesel</b> 和 <b>OG Rune</b>，无论结果如何。</>,
      p2: '如果你决定在铸造窗口开启时销毁 100 枚 Arbuz，协议将从卡组中返回一张 Magic Arbuz 卡。',
      p3: '每张卡都带有独特预言。其艺术、特征和预言均来源于链上卡片索引的哈希。',
      p4: '生成艺术并非托管于服务器 - 它是冻结在比特币区块链中的 JavaScript。当你打开一张卡时，应用从链上获取代码并在浏览器中实时运行。动画与预言直接从比特币上执行。',
      steps: [
        { n: '1', title: '铸造 ARBUZ 代币',       body: '330 sats + 1,069 sats Acai 合约要求 + 网络费。选择 Rebar（私有）或公开内存池。' },
        { n: '2', title: '铸造 MAGIC ARBUZ 卡',   body: <>在开放窗口期间（每 144 个区块），销毁 100 枚 Arbuz。合约通过 <a href="https://github.com/JinMaa/clock-in" target="_blank" rel="noopener noreferrer">clock-in ↗</a> 验证时机。</> },
        { n: '3', title: '持有或发送',             body: '卡片是 alkane - 可在比特币 L1 上转移。每张卡都带有独特预言和生成艺术。' },
      ],
    },
    minting: {
      howTitle: '工作原理',
      howBody:  <>每次 Arbuz 代币铸造构建一个 PSBT，包含一个铭文输出（330 sats）、一个国库输出（1,069 sats Acai 合约要求），以及调用 Arbuz 合约 (2 : 25349) <b>opcode 77</b> 的 protostone。成功时，合约将 <b>100 Arbuz</b> alkane 铸造到你的地址。每次 Arbuz 铸造还会铸造 <b>Diesel</b> 和 <b>OG Rune</b> - 即使 Arbuz 铸造失败也如此。</>,
      routingTitle: '路由',
      routing: [
        { name: 'Rebar - 私有矿池',  color: C.rind,  body: <><b>当前已禁用。</b>已签名交易提交到 Rebar Shield 而非公开内存池。Rebar 将其路由到合作矿池。Rebar 费用输出已包含在 PSBT 中，由你直接支付。<b>成功率约 80%</b> - 参见风险。</> },
        { name: '公开 - 开放内存池', color: C.melon, body: '你的交易被广播到公开内存池。任何矿工都可以打包 - 但只有经批准的矿池才能成功铸造。你永远不知道下一个区块是谁挖出的，因此风险要高得多。' },
      ],
      outputsTitle: '交易输出',
      outputs: [
        ['输出 0', '330 sats → 你的 taproot 地址（铭文）'],
        ['输出 1', 'Protostone（0 sats）- 调用 opcode 77'],
        ['输出 2', '1,069 sats → Acai 合约要求'],
        ['输出 3', '找零 → 你的支付地址'],
        ['输出 4', 'Rebar 费用（仅 Rebar 模式）'],
      ],
      ripeKick: '铸造状态',
      ripe: [
        { tag: 'JUICY',    c: C.rind,  body: '铸造已在链上确认 - 全新 100 ARBUZ 已记入你的账户。' },
        { tag: 'RIPENING', c: C.yellow, body: '仍在内存池中，等待经批准的矿池确认。' },
        { tag: 'SPOILED',  c: C.melon, body: '被未批准的矿池挖出 - 铸造被回滚。手续费不予退还。' },
      ],
    },
    deck: {
      genTitle: '卡片生成',
      genBody:  <>每张卡的特征来源于其索引的 <b>SHA-256 哈希</b>。哈希决定背景、卡片类型、主符号、三个神秘符号、边框颜色、发光颜色和你的每日预言。完整渲染 JS 存储在链上，通过合约 (2 : 69849) 的 <b>opcode 1000</b> 获取。</>,
      typesTitle: '卡片类型',
      types: [
        { name: '经典',  chance: '大多数',     body: '22 张大阿卡纳 - 星星、月亮、太阳、塔、命运之轮、隐者、魔术师、女祭司、皇帝、女皇、恶魔、愚者、教皇、恋人、战车、力量、正义、倒吊人、死神、节制、审判、世界。' },
        { name: '故障',  chance: '~13 / 256', body: '8 张引用 alkanes 生态系统的特殊卡 - Airhead、Mist、Puppet、Taco、Acai、Diesel、Clockin、Cheekyb。始终为金色边框和发光。' },
        { name: '绝对',  chance: '< 1 / 256', body: '极为稀有。只有两种可能：Fartane（银色）或 Arbuz（绿色）。由哈希字节 25 < 1 决定。' },
      ],
      windowTitle: '铸造窗口',
      windowBody:  <>卡片合约在铸造前调用 <b>clock-in</b>（opcode 103）。窗口从第 907,205 区块开始，每 <b>144 个区块</b>开启一次。错过窗口 - 交易失败，你保留 Arbuz 代币。</>,
      predTitle: '预言',
      predBody:  '每张卡都带有独特预言 - 来源于生成艺术的同一链上哈希。点击卡片可在中英文之间切换预言。',
    },
    fees: {
      arbuzTitle: '铸造 ARBUZ',
      arbuzRows: [
        ['铭文输出',    '330 sats（到你的地址）'],
        ['Acai 合约要求', '1,069 sats（每次铸造）'],
        ['网络费',      '~251 vB × 费率'],
        ['Rebar 费',    '~251 vB × Rebar 费率（仅 Rebar 模式）'],
        ['总计（公开）', '1,399 sats + 网络费'],
      ],
      cardTitle: '铸造卡片',
      cardRows: [
        ['铭文输出',    '330 sats（到你的地址）'],
        ['ARBUZ alkane', '100 Arbuz 销毁（由合约持有）'],
        ['网络费',      '~300 vB × 费率'],
        ['总计',        '330 sats + 100 Arbuz + 网络费'],
      ],
    },
    risks: {
      poolsTitle: 'Arbuz Token 允许的矿池',
      poolsBody:  'Acai 合约在不依赖第三方的情况下验证打包交易的矿池 - 这是一个纯智能合约函数，任何人都无法干预。只有经批准矿池挖出的区块才会成功。其他矿池会失败 - 你损失已发送的手续费。',
      publicTitle: 'Public 铸造成功率',
      publicBody:  '虽然你可以仅花几个 sats 提交公开铸造，但失败的概率要高得多 - 你永远不知道下一个区块是谁挖出的。任何不受支持的矿池都可能挖出下一个区块，导致你的铸造失败。',
      rebarTitle: 'Rebar 成功率',
      rebarBody:  '约 20% 的区块仍由不受支持的矿池挖出。如果你的交易落在不受支持的区块中则失败。费用仍需支付。',
      timingTitle: '卡片铸造时机',
      timingBody:  '如果交易在 144 区块窗口外被打包，铸造失败 - 你保留 Arbuz alkane。在窗口开启前提交，费率要足够高以确保落在目标区块。',
      utxoTitle: '混合 UTXO',
      utxoBody:  '若所选 UTXO 包含其他资产，合约会自动将其退还 - 仅消耗 100 ARBUZ。',
    },
  },
};

// ── helpers ──────────────────────────────────────────────────────────────────
// section label - tiny faint uppercase
function SecTitle({ children, color }) {
  return <div style={{...LBL,color:color||C.faint}}>{children}</div>;
}
function Row({ k, v }) {
  return (
    <div style={{display:'grid',gridTemplateColumns:'minmax(0,120px) minmax(0,1fr)',gap:12,padding:'10px 14px'}}>
      <Mon size={11} dim>{k}</Mon>
      <Mon size={12} style={{textAlign:'right',fontWeight:700,overflowWrap:'anywhere'}}>{v}</Mon>
    </div>
  );
}
// cream rows with 2px white separators
function Rows({ rows }) {
  return (
    <div style={{borderRadius:16,overflow:'hidden',background:C.sunk}}>
      {rows.map(([k,v],i)=><div key={k} style={{borderTop:i?`2px solid ${C.card}`:'none'}}><Row k={k} v={v}/></div>)}
    </div>
  );
}
function Block({ title, children }) {
  return <div><SecTitle>{title}</SecTitle><div style={{marginTop:10}}>{children}</div></div>;
}
function Panel({ children }) {
  return <div style={{position:'relative'}}>{children}</div>;
}
// cream block (kept only for the TL;DR panel)
function BoxedPanel({ children }) {
  return <div style={{borderRadius:20,padding:18,background:C.sunk}}>{children}</div>;
}
const subHead = HEAD(15);

// ── sections ─────────────────────────────────────────────────────────────────
function Overview({ c, onLearn }) {
  const ov = c.overview;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:22}}>
      <div style={{fontFamily:F.body,fontSize:15,fontWeight:500,color:C.dim,lineHeight:1.55}}>{ov.lead}</div>
      {onLearn && (
        <button type="button" onClick={onLearn} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:9,width:'100%',marginTop:-8,
          padding:'13px 16px',border:'none',borderRadius:999,cursor:'pointer',whiteSpace:'nowrap',
          fontFamily:F.body,fontWeight:800,fontSize:13.5,color:C.ink,background:C.card,boxShadow:`${SH.line},${SH.pill}`}}>
          <I18nLock en={CONTENT.en.overview.learnCta}>{ov.learnCta}</I18nLock><span style={{fontSize:16,lineHeight:1}}>→</span>
        </button>
      )}
      <BoxedPanel>
        <SecTitle>{ov.tldr}</SecTitle>
        <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:9}}>
          <Bod size={14}>{ov.p1}</Bod>
          <Bod size={13} dim>{ov.p2}</Bod>
          <Bod size={13} dim>{ov.p3}</Bod>
          <Bod size={13} dim>{ov.p4}</Bod>
        </div>
      </BoxedPanel>
      <div style={{display:'flex',flexDirection:'column'}}>
        {ov.steps.map((s,i)=>(
          <div key={s.n} style={{padding:'14px 0',borderTop:i>0?`1px solid ${C.rule}`:'none'}}>
            <Mon size={11} color={C.faint} style={{fontWeight:700}}>0{s.n}</Mon>
            <div style={{marginTop:4,...subHead}}>
              <I18nLock block en={CONTENT.en.overview.steps[i].title}>{s.title}</I18nLock>
            </div>
            <div style={{marginTop:6}}><Bod dim size={13}>{s.body}</Bod></div>
          </div>
        ))}
      </div>
      <Block title={ov.specsTitle}><Rows rows={c.specs}/></Block>
    </div>
  );
}
function Minting({ c }) {
  const m = c.minting;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:22}}>
      <Panel><SecTitle>{m.howTitle}</SecTitle><div style={{marginTop:10}}><Bod size={13} dim>{m.howBody}</Bod></div></Panel>
      <Block title={m.routingTitle}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {m.routing.map(r=>(
            <div key={r.name}>
              <span style={subHead}>{r.name}</span>
              <div style={{marginTop:5}}><Bod size={13} dim>{r.body}</Bod></div>
            </div>
          ))}
        </div>
      </Block>
      <Block title={m.outputsTitle}><Rows rows={m.outputs}/></Block>
      <Block title={m.ripeKick}>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {m.ripe.map(r=>(
            <div key={r.tag} style={{padding:'12px 14px',borderRadius:16,background:C.sunk}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:7}}><Dot color={r.c}/><span style={{fontFamily:F.body,fontWeight:800,fontSize:12.5,letterSpacing:.4,color:C.ink}}>{r.tag}</span></span>
              <div style={{marginTop:4}}><Bod size={12.5} dim>{r.body}</Bod></div>
            </div>
          ))}
        </div>
      </Block>
    </div>
  );
}
function Deck({ c }) {
  const d = c.deck;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:22}}>
      <Panel><SecTitle>{d.genTitle}</SecTitle><div style={{marginTop:10}}><Bod size={13} dim>{d.genBody}</Bod></div></Panel>
      <Block title={d.typesTitle}>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {d.types.map((t)=>(
            <div key={t.name} style={{padding:'12px 14px',borderRadius:16,background:C.sunk}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:10}}>
                <span style={subHead}>{t.name}</span>
                <Mon size={11} dim>{t.chance}</Mon>
              </div>
              <div style={{marginTop:6}}><Bod size={13} dim>{t.body}</Bod></div>
            </div>
          ))}
        </div>
      </Block>
      <Block title={d.windowTitle}><Bod size={13} dim>{d.windowBody}</Bod></Block>
      <Block title={d.predTitle}><Bod size={13} dim>{d.predBody}</Bod></Block>
    </div>
  );
}
function Fees({ c }) {
  const f = c.fees;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:22}}>
      <Block title={f.arbuzTitle}><Rows rows={f.arbuzRows}/></Block>
      <Block title={f.cardTitle}><Rows rows={f.cardRows}/></Block>
    </div>
  );
}
function Risks({ c }) {
  const r = c.risks;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:22}}>
      <Block title={r.poolsTitle}>
        <Bod size={13} dim>{r.poolsBody}</Bod>
        <div style={{marginTop:12,display:'flex',flexWrap:'wrap',gap:6}}>
          {ALLOWED_POOLS.map(p=>(
            <span key={p} style={{padding:'4px 10px',borderRadius:999,background:C.sunk}}><Mon size={11} dim>{p}</Mon></span>
          ))}
        </div>
      </Block>
      <Block title={r.publicTitle}><Bod size={13} dim>{r.publicBody}</Bod></Block>
      <Block title={r.rebarTitle}><Bod size={13} dim>{r.rebarBody}</Bod></Block>
      <Block title={r.timingTitle}><Bod size={13} dim>{r.timingBody}</Bod></Block>
      <Block title={r.utxoTitle}><Bod size={13} dim>{r.utxoBody}</Bod></Block>
    </div>
  );
}

const SECTIONS = { overview: Overview, minting: Minting, deck: Deck, fees: Fees, risks: Risks };

export function MobReadMe({ open, onClose }) {
  const { lang: globalLang } = useLang();
  const [lang, setLang] = useState(globalLang === 'zh' ? 'zh' : 'en');
  const [activeId, setActiveId] = useState('overview');
  const [shown, setShown] = useState(false);
  const navigate = useNavigate();
  const goArbuzino = () => { onClose(); navigate('/?rules=1'); };
  useScrollLock(open);   // pin the background (iOS-safe) while the readme sheet is open

  useEffect(() => {
    if (open) {
      setLang(globalLang === 'zh' ? 'zh' : 'en');
      setActiveId('overview');
      setShown(false);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
  }, [open, globalLang]);

  // Horizontal swipe on the content area pages between README sections
  // (left -> next, right -> prev). Vertical scroll stays untouched (pan-y).
  const swipeRef = useRef(null);
  const goSection = (dir) => {
    const c = CONTENT[lang] || CONTENT.en;
    const i = c.nav.findIndex(n => n.id === activeId);
    const ni = i + dir;
    if (ni >= 0 && ni < c.nav.length) setActiveId(c.nav[ni].id);
  };
  const onSwipeStart = (e) => {
    if (e.touches.length !== 1) { swipeRef.current = null; return; }
    swipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onSwipeEnd = (e) => {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s) return;
    const dx = e.changedTouches[0].clientX - s.x;
    const dy = e.changedTouches[0].clientY - s.y;
    // dominant horizontal move past threshold -> page sections
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) goSection(dx < 0 ? 1 : -1);
  };

  if (!open) return null;
  const c = CONTENT[lang] || CONTENT.en;
  const activeIdx = c.nav.findIndex(n => n.id === activeId);
  const Content = SECTIONS[activeId];

  return (
    <>
    <div onClick={onClose} style={{position:'fixed',inset:0,zIndex:319,background:SCRIM,opacity:shown?1:0,transition:trans('opacity .34s'),pointerEvents:shown?'auto':'none'}}/>
    <div className="mob-rm" style={{position:'fixed',left:0,right:0,bottom:0,top:'max(64px,env(safe-area-inset-top,0px))',zIndex:320,background:C.card,borderTopLeftRadius:20,borderTopRightRadius:20,overflow:'hidden',boxShadow:`0 -1px 0 ${C.rule}`,display:'flex',flexDirection:'column',
      transform:shown?'translateY(0)':'translateY(100%)',transition:trans('transform .34s cubic-bezier(.2,.9,.3,1)')}}>
      <style>{`.mob-rm a{color:${C.ink};font-weight:700;text-decoration:underline;text-decoration-color:${C.faint};text-underline-offset:2px}.mob-rm b{color:${C.ink};font-weight:700}`}</style>

      {/* drag-handle / grabber (tap to close) */}
      <div onClick={onClose} className="tap" style={{flexShrink:0,display:'flex',justifyContent:'center',cursor:'pointer',paddingTop:10,paddingBottom:4}}>
        <div style={{width:36,height:4,borderRadius:2,background:C.rule}}/>
      </div>

      {/* Header — BACK + language on the top row, then the section title */}
      <div style={{padding:'2px 18px 12px',flexShrink:0,display:'flex',flexDirection:'column',gap:10}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
          <span onClick={onClose} className="tap" style={{fontFamily:F.body,fontSize:12.5,fontWeight:800,color:C.ink,cursor:'pointer'}}>{c.back}</span>
          <LangSeg lang={lang} setLang={setLang} />
        </div>
        <div style={{...HEAD(26),letterSpacing:-.8,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{c.nav[activeIdx].label}</div>
      </div>

      {/* Section tabs - one row, the current section as a yellow pill */}
      <div style={{flexShrink:0,padding:'0 12px 10px',borderBottom:`1px solid ${C.rule}`}}>
        <div role="tablist" style={{display:'flex',gap:2,padding:3,borderRadius:999,background:C.card,boxShadow:SH.line,userSelect:'none'}}>
          {c.nav.map((a)=>{
            const on = activeId===a.id;
            return (
              <div key={a.id} role="tab" aria-selected={on} className="tap" onClick={()=>setActiveId(a.id)}
                style={{flex:1,minWidth:0,display:'flex',alignItems:'center',justifyContent:'center',height:32,borderRadius:999,cursor:'pointer',
                  fontFamily:F.body,fontWeight:700,fontSize:11.5,whiteSpace:'nowrap',overflow:'hidden',
                  background:on?C.yellow:'transparent',color:on?C.ink:C.dim,transition:trans('background .25s ease, color .25s ease')}}>
                {a.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div onTouchStart={onSwipeStart} onTouchEnd={onSwipeEnd} style={{flex:1,overflowY:'auto',touchAction:'pan-y',overscrollBehavior:'contain',padding:'20px 18px 24px',animation:anim('mob-screenin .3s ease')}} key={activeId}>
        <Content c={c} onLearn={activeId==='overview'?goArbuzino:undefined}/>
      </div>

      {/* Footer */}
      <div style={{flexShrink:0,padding:'12px 16px max(14px,env(safe-area-inset-bottom,14px))',borderTop:`1px solid ${C.rule}`}}>
        <div style={{display:'flex',gap:6}}>
          {REPOS.map(r=>(
            <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer" style={{flex:1,textDecoration:'none',display:'flex',alignItems:'center',justifyContent:'space-between',gap:4,padding:'7px 10px',borderRadius:999,background:C.card,boxShadow:SH.line,minWidth:0}}>
              <span style={{display:'flex',alignItems:'center',gap:5,minWidth:0}}>
                <GithubMark size={12} color={C.ink}/>
                <span style={{fontFamily:F.mono,fontSize:10,color:C.ink,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.label.split('/')[1]}</span>
              </span>
              <span style={{fontFamily:F.mono,fontSize:10,color:C.faint,flexShrink:0}}>↗</span>
            </a>
          ))}
        </div>
      </div>
    </div>
    </>
  );
}
